'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Skeleton from '@mui/material/Skeleton';
import {Section} from '../primitives';
import {CardSkeleton} from './CardSkeleton';
import {MastheadSkeleton} from './MastheadSkeleton';
import {SectionHeadingSkeleton} from './SectionHeadingSkeleton';
import {TextLinesSkeleton} from './TextLinesSkeleton';

// Mirrors the entity detail pages that share the hero shell (project / phase /
// unit-type): a full-bleed HeroGallery (360/615), then the PropertySummary
// header row (a 100px circular logo + name/location at the start, Register-
// Interest / Take-A-Tour CTAs at the end, space-between), then the raised
// "About property" text section. A related-items carousel sits below the fold.
// Country detail is a different design — see CountryDetailPageSkeleton.
export function DetailPageSkeleton() {
  return (
    <>
      <MastheadSkeleton mobileHeight={360} desktopHeight={615} />
      <Section>
        <Container>
          <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 3}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0}}>
              <Skeleton variant="circular" animation="wave" width={100} height={100} sx={{flexShrink: 0}} />
              <Box sx={{display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0}}>
                <Skeleton variant="text" animation="wave" width={240} sx={{fontSize: theme => theme.font.size['title-3']}} />
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                  <Skeleton variant="rounded" animation="wave" width={14} height={14} />
                  <Skeleton variant="text" animation="wave" width={160} sx={{fontSize: theme => theme.font.size['body-2']}} />
                </Box>
              </Box>
            </Box>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0}}>
              <Skeleton
                variant="rounded"
                animation="wave"
                width={172}
                height={48}
                sx={theme => ({borderRadius: `${theme.radii.button}px`})}
              />
              <Skeleton
                variant="rounded"
                animation="wave"
                width={150}
                height={48}
                sx={theme => ({borderRadius: `${theme.radii.button}px`})}
              />
            </Box>
          </Box>
        </Container>
      </Section>
      <Section tone="raised">
        <Container>
          <SectionHeadingSkeleton width={200} />
          <TextLinesSkeleton />
        </Container>
      </Section>
      <Section tone="subtle">
        <Container>
          <SectionHeadingSkeleton width={220} />
          <Box
            sx={theme => ({
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: theme.spacing(4),
              [theme.breakpoints.up('sm')]: {gridTemplateColumns: 'repeat(2, 1fr)'},
              [theme.breakpoints.up('md')]: {gridTemplateColumns: 'repeat(3, 1fr)'},
            })}
          >
            {Array.from({length: 3}, (_, i) => (
              <CardSkeleton key={i} />
            ))}
          </Box>
        </Container>
      </Section>
    </>
  );
}

DetailPageSkeleton.displayName = 'DetailPageSkeleton';
